<template>
  <div class="min-h-screen hero-gradient flex items-center justify-center px-6">
    <div class="floating-shapes"></div>
    
    <div class="w-full max-w-md relative z-10">
      <!-- Back to Home -->
      <router-link to="/" class="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors">
        <ArrowLeftIcon class="w-4 h-4 mr-2" />
        Back to Home
      </router-link>
      
      <div class="card-glass p-8">
        <div class="text-center mb-8">
          <div class="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <QrCodeIcon class="w-8 h-8 text-white" />
          </div>
          <h1 class="text-3xl font-bold text-gradient mb-2">Welcome Back</h1>
          <p class="text-gray-300">Sign in to your Digital Menu AI account</p>
        </div>
        
        <form @submit.prevent="login" class="space-y-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
              placeholder="Enter your email"
            >
          </div>
          
          <div>
            <label for="password" class="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <div class="relative">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="w-full px-4 py-3 bg-dark-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                placeholder="Enter your password"
              >
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                <EyeOffIcon v-else class="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div class="flex items-center justify-between">
            <label class="flex items-center">
              <input
                v-model="form.rememberMe"
                type="checkbox"
                class="w-4 h-4 text-primary-500 bg-dark-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2"
              >
              <span class="ml-2 text-sm text-gray-300">Remember me</span>
            </label>
            <router-link to="/forgot-password" class="text-sm text-primary-400 hover:text-primary-300">Forgot password?</router-link>
          </div>
          
          <button
            type="submit"
            :disabled="loading"
            class="w-full btn-primary"
            :class="{ 'opacity-50 cursor-not-allowed': loading }"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Signing In...
            </span>
            <span v-else>Sign In</span>
          </button>
        </form>
        
        <div class="mt-8 text-center">
          <p class="text-gray-300">
            Don't have an account?
            <router-link to="/register" class="text-primary-400 hover:text-primary-300 font-medium">Sign up</router-link>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { QrCodeIcon, ArrowLeftIcon, EyeIcon, EyeOffIcon } from 'lucide-vue-next'
import { apiService } from '../services/api'
import { useToast } from '@/composables/useToast'

export default {
  name: 'Login',
  components: {
    QrCodeIcon,
    ArrowLeftIcon,
    EyeIcon,
    EyeOffIcon
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  data() {
    return {
      form: {
        email: '',
        password: '',
        rememberMe: false
      },
      showPassword: false,
      loading: false
    }
  },
  methods: {
    async login() {
      this.loading = true
      try {
        const response = await apiService.login(this.form.email, this.form.password)
        
        // Store authentication data (C# backend returns PascalCase properties)
        localStorage.setItem('authToken', response.Token)
        localStorage.setItem('user', JSON.stringify(response.User))
        this.$router.push('/dashboard')
      } catch (error) {
        console.error('Login error:', error)
        this.toast.error('Login Failed', error.message || 'Please check your credentials and try again')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
